// client/src/utils/match.js
import { MATCH_STATUS, isCompletedStatus } from './matchStatus'

function toPlayerArray(players) {
  if (Array.isArray(players)) return players
  if (typeof players === 'string') {
    try {
      const parsed = JSON.parse(players)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

function sideFallbackPlayer(match, side) {
  if (side === 'home') {
    if (!match?.home_player_id) return null
    return {
      id: match.home_player_id,
      first_name: match.home_first_name,
      last_name: match.home_last_name,
    }
  }
  if (!match?.away_player_id) return null
  return {
    id: match.away_player_id,
    first_name: match.away_first_name,
    last_name: match.away_last_name,
  }
}

function formatSideName(players, fallbackPlayer = null) {
  const names = (players || [])
    .map((player) =>
      `${player?.first_name || ''} ${player?.last_name || ''}`.trim(),
    )
    .filter(Boolean)
  if (names.length > 0) return names.join(' / ')

  const fallbackName = fallbackPlayer
    ? `${fallbackPlayer.first_name || ''} ${fallbackPlayer.last_name || ''}`.trim()
    : ''
  if (fallbackName) return fallbackName
  return fallbackPlayer?.id ? `Player #${fallbackPlayer.id}` : 'Opponent'
}

export function getMatchSidePlayers(match, side) {
  if (!match || !side) return []
  const players = toPlayerArray(match[`${side}_players`])
  if (players.length > 0) return players
  const fallbackPlayer = sideFallbackPlayer(match, side)
  return fallbackPlayer ? [fallbackPlayer] : []
}

export function isPlayerOnMatchSide(match, playerId, side = 'both') {
  if (!match || !playerId) return false

  const onSide = (targetSide) =>
    getMatchSidePlayers(match, targetSide).some(
      (player) => Number(player?.id) === Number(playerId),
    )

  if (side === 'home' || side === 'away') {
    return onSide(side)
  }

  return onSide('home') || onSide('away')
}

/**
 * Get match status summary for an array of matches
 * @param {Array} matches - Array of match objects
 * @returns {Object} Summary object
 */
export function getMatchStatusSummary(matches) {
  if (!Array.isArray(matches))
    return {
      total: 0,
      scheduled: 0,
      completed: 0,
      unscheduled: 0,
      byStatus: {},
    }

  const summary = {
    total: matches.length,
    scheduled: 0,
    completed: 0,
    unscheduled: 0,
    byStatus: {},
  }

  matches.forEach((match) => {
    const status = match?.status || MATCH_STATUS.UNKNOWN
    summary.byStatus[status] = (summary.byStatus[status] || 0) + 1

    switch (status) {
      case MATCH_STATUS.SCHEDULED:
        summary.scheduled++
        break
      default:
        if (isCompletedStatus(status)) {
          summary.completed++
        } else {
          summary.unscheduled++
        }
    }
  })

  return summary
}

/**
 * Format match score for display relative to a player
 * @param {Object} match - Match object with set_scores
 * @param {number} currentPlayerId - ID of the current player
 * @returns {string} Formatted score string
 */
export function formatMatchScore(match, currentPlayerId) {
  const scores = match?.set_scores || []
  if (!scores.length) return 'No score recorded'

  const isCurrentUserHome = isPlayerOnMatchSide(match, currentPlayerId, 'home')

  const formattedSets = scores
    .filter((set) => typeof set.set_number === 'number')
    .sort((a, b) => a.set_number - b.set_number)
    .filter((set) => (set.home_games || 0) > 0 || (set.away_games || 0) > 0)
    .map((set) => {
      const userScore = isCurrentUserHome ? set.home_games : set.away_games
      const opponentScore = isCurrentUserHome ? set.away_games : set.home_games
      return `${userScore}-${opponentScore}`
    })

  return formattedSets.length > 0
    ? formattedSets.join(', ')
    : 'No score recorded'
}

/**
 * Get opponent name for a match relative to a player
 * @param {Object} match - Match object
 * @param {number} currentPlayerId - ID of the current player
 * @returns {string} Opponent's name
 */
export function getOpponentName(match, currentPlayerId) {
  if (!match) return 'Opponent'
  const isHome = isPlayerOnMatchSide(match, currentPlayerId, 'home')
  const isAway = isPlayerOnMatchSide(match, currentPlayerId, 'away')
  if (!isHome && !isAway) return 'Opponent'
  const opponentSide = isHome && !isAway ? 'away' : 'home'
  const opponentPlayers = getMatchSidePlayers(match, opponentSide)
  const fallbackPlayer = sideFallbackPlayer(match, opponentSide)
  return formatSideName(opponentPlayers, fallbackPlayer)
}

/**
 * Get player's total score (games won) for a match
 * @param {Object} match - Match object with set_scores
 * @param {number} playerId - ID of the player
 * @returns {number} Total games won by the player
 */
export function getPlayerScore(match, playerId) {
  const scores = match?.set_scores || []
  if (!scores.length) return 0

  const isPlayerHome = isPlayerOnMatchSide(match, playerId, 'home')

  return scores.reduce((total, set) => {
    const playerGames = isPlayerHome ? set.home_games || 0 : set.away_games || 0
    return total + playerGames
  }, 0)
}

/**
 * Get opponent's total score (games won) for a match relative to a player
 * @param {Object} match - Match object with set_scores
 * @param {number} playerId - ID of the current player
 * @returns {number} Total games won by the opponent
 */
export function getOpponentScore(match, playerId) {
  const scores = match?.set_scores || []
  if (!scores.length) return 0

  const isPlayerHome = isPlayerOnMatchSide(match, playerId, 'home')

  return scores.reduce((total, set) => {
    const opponentGames = isPlayerHome
      ? set.away_games || 0
      : set.home_games || 0
    return total + opponentGames
  }, 0)
}

/**
 * Determine match outcome for a given player, accounting for retirement statuses
 * @param {Object} match - Match object (expects status, home_player_id, away_player_id, submitted_by_player_id, set_scores)
 * @param {number} playerId - Player ID to evaluate
 * @returns {'win'|'loss'|'unknown'} Outcome from the player's perspective
 */
export function getMatchOutcomeForPlayer(match, playerId) {
  if (!match || !isCompletedStatus(match.status)) return 'unknown'

  const status = match.status
  const scores = match.set_scores || []
  const isHomeSide = isPlayerOnMatchSide(match, playerId, 'home')
  const isAwaySide = isPlayerOnMatchSide(match, playerId, 'away')

  if (!isHomeSide && !isAwaySide) return 'unknown'

  const resolveFromWinnerId = () => {
    const winnerId = Number(match.winner_id)
    if (!Number.isFinite(winnerId) || winnerId <= 0) return null

    if (isPlayerOnMatchSide(match, winnerId, 'home')) {
      return isHomeSide ? 'win' : 'loss'
    }
    if (isPlayerOnMatchSide(match, winnerId, 'away')) {
      return isAwaySide ? 'win' : 'loss'
    }
    return null
  }

  // Normal completed match with scores: decide by sets won
  if (status === MATCH_STATUS.NORMAL) {
    if (!scores.length) return resolveFromWinnerId() || 'unknown'
    let homeSets = 0
    let awaySets = 0
    scores.forEach((set) => {
      const hg = set.home_games || 0
      const ag = set.away_games || 0
      if (hg > ag) homeSets++
      else if (ag > hg) awaySets++
    })
    if (homeSets === awaySets) return resolveFromWinnerId() || 'unknown'
    const homeWon = homeSets > awaySets
    if (homeWon) return isHomeSide ? 'win' : 'loss'
    return isAwaySide ? 'win' : 'loss'
  }

  if (status === MATCH_STATUS.HOME_RETIRED) {
    return isHomeSide ? 'loss' : 'win'
  }

  if (status === MATCH_STATUS.AWAY_RETIRED) {
    return isAwaySide ? 'loss' : 'win'
  }

  // Retirement/default outcomes with submitter-relative statuses
  const submittedBy = match.submitted_by_player_id

  if (status === MATCH_STATUS.I_RETIRED) {
    // Submitter retired => opponent wins
    if (!submittedBy) return 'unknown'
    const submitterIsHome = isPlayerOnMatchSide(match, submittedBy, 'home')
    const submitterIsAway = isPlayerOnMatchSide(match, submittedBy, 'away')
    if (!submitterIsHome && !submitterIsAway) return 'unknown'
    if (submitterIsHome) return isHomeSide ? 'loss' : 'win'
    return isAwaySide ? 'loss' : 'win'
  } else if (status === MATCH_STATUS.OPPONENT_RETIRED) {
    // Opponent of submitter retired => submitter wins
    if (!submittedBy) return 'unknown'
    const submitterIsHome = isPlayerOnMatchSide(match, submittedBy, 'home')
    const submitterIsAway = isPlayerOnMatchSide(match, submittedBy, 'away')
    if (!submitterIsHome && !submitterIsAway) return 'unknown'
    if (submitterIsHome) return isHomeSide ? 'win' : 'loss'
    return isAwaySide ? 'win' : 'loss'
  }

  return 'unknown'
}

/**
 * Find a match between two players
 * @param {Array} matches - Array of match objects
 * @param {number} playerId - ID of the first player
 * @param {number} opponentId - ID of the second player
 * @returns {Object|null} Match object or null if not found
 */
export function findMatchBetweenPlayers(matches, playerId, opponentId) {
  if (!matches || !playerId || !opponentId) return null
  return matches.find(
    (m) =>
      (isPlayerOnMatchSide(m, playerId, 'home') &&
        isPlayerOnMatchSide(m, opponentId, 'away')) ||
      (isPlayerOnMatchSide(m, playerId, 'away') &&
        isPlayerOnMatchSide(m, opponentId, 'home')),
  )
}

/**
 * Determine home/away status for a match relative to a player
 * @param {Object|null} match - Match object
 * @param {number} currentPlayerId - ID of the current player
 * @returns {Object} Object with isHome, isAway, shouldShow, icon, and text properties
 */
export function getHomeAwayStatus(match, currentPlayerId) {
  if (!match || !currentPlayerId) {
    return {
      isHome: false,
      isAway: false,
      shouldShow: false,
      icon: '',
      text: '',
    }
  }

  const isHome = isPlayerOnMatchSide(match, currentPlayerId, 'home')
  const isAway = isPlayerOnMatchSide(match, currentPlayerId, 'away')

  return {
    isHome,
    isAway,
    shouldShow: isHome || isAway,
    icon: isHome ? '🏠' : isAway ? '🏃' : '',
    text: isHome ? 'Home' : isAway ? 'Away' : '',
  }
}
